import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  promptsV2Atom,
  filteredPromptsV2Atom,
  pipelineErrorAtom,
  prepareStatusAtom,
  processStatusAtom,
  applyStatusAtom,
  prepareResultAtom,
  processResultAtom,
  selectedNodeResultsAtom,
  applyResultAtom,
  selectedPromptAtom,
  selectedModelAtom,
  selectedProviderAtom,
  copyTreeAtom,
  pipelineStatusAtom,
  processModeAtom,
  batchModeAtom,
  fieldTitleAtom,
  fieldDescriptionAtom,
  fieldBodyValueAtom,
  studentFacingAtom,
  canPrepareStartAtom,
  canProcessStartAtom,
  selectedModelValueAtom,
  promptRunResponseAtom,
  promptRunRequestParamsAtom,
  promptRunErrorAtom,
  promptRunRunningAtom,
  promptRunRawResultJsonAtom,
} from './atoms'
import {
  getPromptsV2,
  getPipelineStatus,
  startPrepare,
  getPrepareResults,
  startProcess,
  getProcessResults,
  logProcessReport,
  startApply,
  getApplyResults,
  removePipeline,
  runPrompt,
} from './utils'
import {
  NodeCorrection,
  ProcessMode,
  BatchMode,
  ProcessResultData,
  ProcessResult,
  CorrectionEntryItem,
  UnchangedEntryItem,
  StageStatus,
} from './types'
import { currentUserResponseAtom } from 'infrastructure/auth/auth.atoms'

/**
 * Custom hook that returns a stable callback for checking pipeline status
 * Polls every 3 seconds continuously, stops on pipeline error
 */
export function usePipelineStatusPolling() {
  const setPrepareStatus = useSetAtom(prepareStatusAtom)
  const setProcessStatus = useSetAtom(processStatusAtom)
  const setApplyStatus = useSetAtom(applyStatusAtom)
  const setError = useSetAtom(pipelineErrorAtom)
  const setPipelineStatus = useSetAtom(pipelineStatusAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const prepareStatus = useAtomValue(prepareStatusAtom)
  const processStatus = useAtomValue(processStatusAtom)
  const applyStatus = useAtomValue(applyStatusAtom)
  const errorRef = useRef(pipelineError)

  // Keep ref in sync with current error value
  useEffect(() => {
    errorRef.current = pipelineError
  }, [pipelineError])

  // Helper to check if status values have changed
  const hasStatusChanged = useCallback(
    (oldStatus: StageStatus | null, newStatus: StageStatus | null): boolean => {
      if (oldStatus === newStatus) return false
      if (!oldStatus && !newStatus) return false
      if (!oldStatus || !newStatus) return true

      // Compare key properties
      return (
        oldStatus.completed !== newStatus.completed ||
        oldStatus.dependant_jobs.total !== newStatus.dependant_jobs.total ||
        oldStatus.dependant_jobs.completed !==
          newStatus.dependant_jobs.completed ||
        oldStatus.worker_jobs.total !== newStatus.worker_jobs.total ||
        oldStatus.worker_jobs.completed !== newStatus.worker_jobs.completed
      )
    },
    [],
  )

  // Helper to update a stage status if it has changed
  const updateStageStatus = useCallback(
    (
      oldStatus: StageStatus | null,
      newStatus: StageStatus | null,
      setStatus: (status: StageStatus | null) => void,
      stageName: string,
    ) => {
      if (!hasStatusChanged(oldStatus, newStatus)) {
        return
      }

      setStatus(newStatus)
      if (newStatus?.completed) {
        setPipelineStatus(`${stageName} stage completed.`)
      }
    },
    [hasStatusChanged, setPipelineStatus],
  )

  // Helper to stop polling
  const stopPolling = useCallback((intervalId: NodeJS.Timeout | null) => {
    if (intervalId) {
      clearInterval(intervalId)
    }
    return null
  }, [])

  // Helper to handle error and stop polling
  const handlePollingError = useCallback(
    (
      err: unknown,
      intervalId: NodeJS.Timeout | null,
    ): NodeJS.Timeout | null => {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Error getting pipeline status: ${errorMessage}`)
      return stopPolling(intervalId)
    },
    [setError, stopPolling],
  )

  return useCallback(
    (treeId: string, versionId: string) => {
      let intervalId: NodeJS.Timeout | null = null

      const checkStatus = async () => {
        // Don't poll if there's a pipeline error
        if (errorRef.current) {
          intervalId = stopPolling(intervalId)
          return
        }

        try {
          const result = await getPipelineStatus(treeId, versionId)
          setError(null)

          if (!result?.data) {
            return
          }

          // Update each stage status if changed
          updateStageStatus(
            prepareStatus,
            result.data.prepare,
            setPrepareStatus,
            'Prepare',
          )
          updateStageStatus(
            processStatus,
            result.data.process,
            setProcessStatus,
            'Process',
          )
          updateStageStatus(
            applyStatus,
            result.data.apply,
            setApplyStatus,
            'Apply',
          )
        } catch (err) {
          intervalId = handlePollingError(err, intervalId)
        }
      }

      // Start polling immediately and continue every 3 seconds
      // Polling will stop if pipelineError is set
      checkStatus()
      intervalId = setInterval(checkStatus, 3000)

      // Return cleanup function
      return () => {
        if (intervalId) {
          clearInterval(intervalId)
        }
      }
    },
    [
      prepareStatus,
      processStatus,
      applyStatus,
      updateStageStatus,
      stopPolling,
      handlePollingError,
      setError,
    ],
  )
}

// Helper function to get sort order for field names
const getFieldSortOrder = (fieldName: string): number => {
  const normalized = fieldName.toLowerCase()
  if (normalized === 'title') return 1
  if (normalized === 'description') return 2
  if (normalized === 'body' || normalized === 'body_value') return 3
  return 4
}

/**
 * Custom hook to manage correction selection state and atom updates
 * Handles the complex logic of initializing all selections and managing user interactions
 */
export function useCorrectionSelection(allCorrections: CorrectionEntryItem[]) {
  const setSelectedNodeResults = useSetAtom(selectedNodeResultsAtom)
  const [userSelectedCorrections, setUserSelectedCorrections] = useState<
    Set<string>
  >(new Set())
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  // Derive selected corrections: all selected by default, user selections after interaction
  const selectedCorrections = useMemo(() => {
    if (hasUserInteracted) {
      return userSelectedCorrections
    }
    // Default to all selected when corrections first become available
    return new Set(allCorrections.map((c) => c.key))
  }, [allCorrections, hasUserInteracted, userSelectedCorrections])

  // Update atom when selectedCorrections changes
  const updateSelectedNodeResults = useCallback(
    (newSelected: Set<string>) => {
      if (allCorrections.length === 0) return

      // Create a record of only selected node corrections
      // Expand each selected entry to create node corrections for all node_ids
      const selectedNodeCorrections: Record<string, NodeCorrection[]> = {}

      allCorrections
        .filter((correction) => newSelected.has(correction.key))
        .forEach((correction) => {
          correction.entry.node_ids.forEach((nodeId) => {
            if (!selectedNodeCorrections[nodeId]) {
              selectedNodeCorrections[nodeId] = []
            }
            selectedNodeCorrections[nodeId].push({
              corrected_value: correction.entry.corrected_value,
              diff: correction.entry.diff,
              field_name: correction.entry.field_name,
              orig_value: correction.entry.orig_value,
            })
          })
        })

      setSelectedNodeResults(selectedNodeCorrections)
    },
    [allCorrections, setSelectedNodeResults],
  )

  // Update atom whenever selectedCorrections changes
  useMemo(() => {
    updateSelectedNodeResults(selectedCorrections)
  }, [selectedCorrections, updateSelectedNodeResults])

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!hasUserInteracted) {
        // First user interaction: initialize with current selected state
        setUserSelectedCorrections(new Set(selectedCorrections))
        setHasUserInteracted(true)
      }

      const newSelected = checked
        ? new Set(allCorrections.map((c) => c.key))
        : new Set<string>()
      setUserSelectedCorrections(newSelected)
    },
    [hasUserInteracted, selectedCorrections, allCorrections],
  )

  const handleSelectCorrection = useCallback(
    (key: string, checked: boolean) => {
      if (!hasUserInteracted) {
        // First user interaction: initialize with current selected state
        setUserSelectedCorrections(new Set(selectedCorrections))
        setHasUserInteracted(true)

        // Use current selectedCorrections as base for the new selection
        const newSelected = new Set(selectedCorrections)
        if (checked) {
          newSelected.add(key)
        } else {
          newSelected.delete(key)
        }
        setUserSelectedCorrections(newSelected)
      } else {
        // Subsequent interactions: use current userSelectedCorrections
        const newSelected = new Set(userSelectedCorrections)
        if (checked) {
          newSelected.add(key)
        } else {
          newSelected.delete(key)
        }
        setUserSelectedCorrections(newSelected)
      }
    },
    [hasUserInteracted, selectedCorrections, userSelectedCorrections],
  )

  return {
    selectedCorrections,
    handleSelectAll,
    handleSelectCorrection,
  }
}

/**
 * Custom hook to fetch and manage AI prompts v2
 * Handles fetching all prompts v2 and storing in atom
 */
export function usePromptsV2() {
  const [promptsV2, setPromptsV2] = useAtom(promptsV2Atom)
  const setPipelineError = useSetAtom(pipelineErrorAtom)

  useEffect(() => {
    if (promptsV2 === null) {
      const fetchPromptsV2 = async () => {
        try {
          const response = await getPromptsV2()
          setPromptsV2(response.data)
        } catch (error) {
          setPipelineError(
            `Error fetching prompts v2: ${error instanceof Error ? error.message : 'Unknown error'}`,
          )
        }
      }

      fetchPromptsV2()
    }
  }, [promptsV2, setPromptsV2, setPipelineError])

  return { promptsV2 }
}

/**
 * Custom hook to manage filtered prompts v2 by area
 * Uses derived atom to filter prompts based on selected area
 */
export function useFilteredPromptsV2() {
  const filteredPrompts = useAtomValue(filteredPromptsV2Atom)
  const { promptsV2 } = usePromptsV2()

  return {
    filteredPrompts,
    loading: promptsV2 === null,
  }
}

/**
 * Custom hook to manage the Prepare stage
 * Handles starting the prepare stage, fetching results, and managing copy tree state
 */
export function usePrepareStage(treeId: string, versionId: string) {
  const prepareStatus = useAtomValue(prepareStatusAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const prepareResult = useAtomValue(prepareResultAtom)
  const copyTree = useAtomValue(copyTreeAtom)
  const processMode = useAtomValue(processModeAtom)
  const fieldTitle = useAtomValue(fieldTitleAtom)
  const fieldDescription = useAtomValue(fieldDescriptionAtom)
  const fieldBodyValue = useAtomValue(fieldBodyValueAtom)
  const studentFacing = useAtomValue(studentFacingAtom)
  const setPipelineError = useSetAtom(pipelineErrorAtom)
  const setPrepareResult = useSetAtom(prepareResultAtom)
  const setCopyTree = useSetAtom(copyTreeAtom)
  const setPipelineStatus = useSetAtom(pipelineStatusAtom)
  const [isStarting, setIsStarting] = useState(false)

  const handleStartStage = useCallback(async () => {
    if (isStarting) return // Prevent double-clicks

    setIsStarting(true)
    try {
      setPipelineStatus('Prepare stage started...')
      await startPrepare(
        treeId,
        versionId,
        copyTree,
        processMode,
        fieldTitle,
        fieldDescription,
        fieldBodyValue,
        studentFacing,
      )
    } catch (error) {
      setPipelineError(
        `Error starting prepare stage: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    } finally {
      setIsStarting(false)
    }
  }, [
    treeId,
    versionId,
    copyTree,
    processMode,
    fieldTitle,
    fieldDescription,
    fieldBodyValue,
    studentFacing,
    isStarting,
    setPipelineError,
    setPipelineStatus,
  ])

  const handleCopyTreeChange = useCallback(
    (checked: boolean) => {
      setCopyTree(checked)
    },
    [setCopyTree],
  )

  // Fetch prepare results when prepareStatus changes
  useEffect(() => {
    const fetchResults = async () => {
      if (!pipelineError && prepareStatus?.completed && !prepareResult) {
        try {
          const results = await getPrepareResults(treeId, versionId)
          if (results.data) {
            setPrepareResult(results.data)
          }
        } catch (error) {
          setPipelineError(
            `Error fetching prepare results: ${error instanceof Error ? error.message : 'Unknown error'}`,
          )
        }
      }
    }

    fetchResults()
  }, [
    prepareStatus,
    pipelineError,
    prepareResult,
    treeId,
    versionId,
    setPrepareResult,
    setPipelineError,
  ])

  const canStart = useAtomValue(canPrepareStartAtom) && !isStarting

  return {
    prepareStatus,
    pipelineError,
    prepareResult,
    copyTree,
    handleStartStage,
    handleCopyTreeChange,
    canStart,
  }
}

/**
 * Custom hook to manage the Process stage
 * Handles starting the process stage and fetching results
 */
export function useProcessStage(treeId: string, versionId: string) {
  const processStatus = useAtomValue(processStatusAtom)
  const prepareStatus = useAtomValue(prepareStatusAtom)
  const selectedPrompt = useAtomValue(selectedPromptAtom)
  const selectedModel = useAtomValue(selectedModelAtom)
  const selectedProvider = useAtomValue(selectedProviderAtom)
  const processMode = useAtomValue(processModeAtom)
  const batchMode = useAtomValue(batchModeAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const prepareResult = useAtomValue(prepareResultAtom)
  const processResult = useAtomValue(processResultAtom)
  const currentUserResponse = useAtomValue(currentUserResponseAtom)
  const setPipelineError = useSetAtom(pipelineErrorAtom)
  const setProcessResult = useSetAtom(processResultAtom)
  const setPipelineStatus = useSetAtom(pipelineStatusAtom)

  const handleStartStage = useCallback(async () => {
    const firstResult = prepareResult?.results?.[0]
    if (!firstResult?.result) {
      setPipelineError('Prepare results not available')
      return
    }

    // Validate required fields for ai-autocorrect mode
    if (processMode === 'ai-autocorrect') {
      if (!selectedPrompt) {
        setPipelineError('No prompt selected')
        return
      }
      if (!selectedModel) {
        setPipelineError('No model selected')
        return
      }
      if (!selectedProvider) {
        setPipelineError('No provider selected')
        return
      }
    }

    const area =
      processMode === 'title-autocorrect' ? '' : selectedPrompt?.area || ''
    const promptId =
      processMode === 'title-autocorrect' ? '' : selectedPrompt?.promptId || ''
    const model = processMode === 'title-autocorrect' ? '' : selectedModel || ''
    const provider =
      processMode === 'title-autocorrect'
        ? 'openrouter'
        : selectedProvider || 'openrouter'

    try {
      setPipelineStatus('Process stage started...')
      await startProcess(
        treeId,
        versionId,
        firstResult.result.work_tree_id,
        firstResult.result.work_tree_ver,
        processMode,
        area,
        promptId,
        model,
        provider,
        batchMode,
      )
    } catch (error) {
      setPipelineError(
        `Error starting process stage: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }, [
    treeId,
    versionId,
    prepareResult,
    selectedPrompt,
    selectedModel,
    selectedProvider,
    processMode,
    batchMode,
    setPipelineError,
    setPipelineStatus,
  ])

  // Fetch process results when processStatus changes
  useEffect(() => {
    const fetchResults = async () => {
      if (!pipelineError && processStatus?.completed && !processResult) {
        try {
          const results = await getProcessResults(treeId, versionId)
          if (results.data) {
            // Log process report after results are loaded
            if (
              currentUserResponse.state === 'hasData' &&
              currentUserResponse.data
            ) {
              try {
                await logProcessReport(
                  treeId,
                  versionId,
                  currentUserResponse.data.email,
                ) // Background s3 logging
              } catch {
                // Ignore logging errors - this is background functionality
              }
            }

            setProcessResult(results.data)
          }
        } catch (error) {
          setPipelineError(
            `Error fetching process results: ${error instanceof Error ? error.message : 'Unknown error'}`,
          )
        }
      }
    }

    fetchResults()
  }, [
    processStatus,
    pipelineError,
    processResult,
    treeId,
    versionId,
    currentUserResponse,
    setProcessResult,
    setPipelineError,
  ])

  const canStart = useAtomValue(canProcessStartAtom)

  return {
    processStatus,
    prepareStatus,
    selectedPrompt,
    pipelineError,
    prepareResult,
    processResult,
    handleStartStage,
    canStart,
  }
}

/**
 * Custom hook to manage the Apply stage
 * Handles starting the apply stage and fetching results
 */
export function useApplyStage(treeId: string, versionId: string) {
  const applyStatus = useAtomValue(applyStatusAtom)
  const processStatus = useAtomValue(processStatusAtom)
  const processResult = useAtomValue(processResultAtom)
  const selectedNodeResults = useAtomValue(selectedNodeResultsAtom)
  const applyResult = useAtomValue(applyResultAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const setPipelineError = useSetAtom(pipelineErrorAtom)
  const setApplyResult = useSetAtom(applyResultAtom)
  const setPipelineStatus = useSetAtom(pipelineStatusAtom)

  const handleStartStage = useCallback(async () => {
    const firstResult = processResult?.results?.[0]
    if (!firstResult?.result) {
      setPipelineError('Process results not available')
      return
    }

    if (!selectedNodeResults || Object.keys(selectedNodeResults).length === 0) {
      setPipelineError('No corrections selected')
      return
    }

    try {
      setPipelineStatus('Apply stage started...')
      await startApply(
        treeId,
        versionId,
        firstResult.result.meta.work_tree_id,
        firstResult.result.meta.work_tree_ver,
        selectedNodeResults,
      )
    } catch (error) {
      setPipelineError(
        `Error starting apply stage: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }, [
    treeId,
    versionId,
    processResult,
    selectedNodeResults,
    setPipelineError,
    setPipelineStatus,
  ])

  // Fetch apply results when applyStatus changes
  useEffect(() => {
    const fetchResults = async () => {
      if (!pipelineError && applyStatus?.completed && !applyResult) {
        try {
          const results = await getApplyResults(treeId, versionId)
          if (results.data) {
            setApplyResult(results.data)
          }
        } catch (error) {
          setPipelineError(
            `Error fetching apply results: ${error instanceof Error ? error.message : 'Unknown error'}`,
          )
        }
      }
    }

    fetchResults()
  }, [
    applyStatus,
    pipelineError,
    applyResult,
    treeId,
    versionId,
    setApplyResult,
    setPipelineError,
  ])

  return {
    applyStatus,
    processStatus,
    processResult,
    selectedNodeResults,
    applyResult,
    pipelineError,
    handleStartStage,
    canStart:
      pipelineError === null &&
      applyStatus === null &&
      processStatus?.completed === true &&
      processResult?.results?.[0]?.result !== undefined &&
      selectedNodeResults !== null &&
      Object.keys(selectedNodeResults).length > 0,
  }
}

/**
 * Custom hook to manage pipeline status UI state and operations
 * Handles polling, status display, and pipeline removal
 */
export function usePipelineStatus(treeId: string, versionId: string) {
  const [pipelineStatus, setPipelineStatus] = useAtom(pipelineStatusAtom)
  const [error, setError] = useAtom(pipelineErrorAtom)
  const setPrepareStatus = useSetAtom(prepareStatusAtom)
  const setProcessStatus = useSetAtom(processStatusAtom)
  const setApplyStatus = useSetAtom(applyStatusAtom)
  const setPrepareResult = useSetAtom(prepareResultAtom)
  const setProcessResult = useSetAtom(processResultAtom)
  const setApplyResult = useSetAtom(applyResultAtom)
  const setSelectedNodeResults = useSetAtom(selectedNodeResultsAtom)
  const setSelectedPrompt = useSetAtom(selectedPromptAtom)
  const setSelectedModel = useSetAtom(selectedModelAtom)
  const setSelectedProvider = useSetAtom(selectedProviderAtom)
  const setCopyTree = useSetAtom(copyTreeAtom)
  const setPromptRunResponse = useSetAtom(promptRunResponseAtom)
  const setPromptRunRequestParams = useSetAtom(promptRunRequestParamsAtom)
  const setPromptRunError = useSetAtom(promptRunErrorAtom)
  const setPromptRunRunning = useSetAtom(promptRunRunningAtom)

  const checkStatus = usePipelineStatusPolling()

  useEffect(() => {
    const cleanup = checkStatus(treeId, versionId)
    return cleanup
  }, [treeId, versionId, checkStatus])

  const handleRemovePipeline = useCallback(async () => {
    try {
      setPromptRunRunning(false)
      await removePipeline(treeId, versionId)
      setPrepareStatus(null)
      setProcessStatus(null)
      setApplyStatus(null)
      setPrepareResult(null)
      setProcessResult(null)
      setApplyResult(null)
      setSelectedNodeResults(null)
      setSelectedPrompt(null)
      setSelectedModel('')
      setSelectedProvider('openrouter')
      setCopyTree(false)
      setPromptRunResponse(null)
      setPromptRunRequestParams(null)
      setPromptRunError(null)
      setPipelineStatus('Successfully removed pipeline.')
      setError(null)
    } catch (error) {
      setPipelineStatus(
        `Error removing pipeline: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }, [
    treeId,
    versionId,
    setPromptRunRunning,
    setPrepareStatus,
    setProcessStatus,
    setApplyStatus,
    setPrepareResult,
    setProcessResult,
    setApplyResult,
    setSelectedNodeResults,
    setSelectedPrompt,
    setSelectedModel,
    setSelectedProvider,
    setCopyTree,
    setPromptRunResponse,
    setPromptRunRequestParams,
    setPromptRunError,
    setPromptRunRunning,
    setPipelineStatus,
    setError,
  ])

  return {
    pipelineStatus,
    error,
    handleRemovePipeline,
  }
}

/**
 * Custom hook to manage provider selection and sync with prompt
 */
export function useProviderSelect() {
  const [selectedProvider, setSelectedProvider] = useAtom(selectedProviderAtom)
  const selectedPrompt = useAtomValue(selectedPromptAtom)
  const prevPromptIdRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!selectedPrompt) {
      prevPromptIdRef.current = null
      return
    }

    const currentPromptId = selectedPrompt.promptId
    const currentProvider = selectedPrompt.metadata?.provider

    if (!currentProvider) {
      return
    }

    const promptChanged = prevPromptIdRef.current !== currentPromptId
    const providerMismatch = selectedProvider !== currentProvider

    if (promptChanged) {
      setSelectedProvider(currentProvider)
      prevPromptIdRef.current = currentPromptId
    } else if (
      providerMismatch &&
      prevPromptIdRef.current === currentPromptId
    ) {
      setSelectedProvider(currentProvider)
    }
  }, [selectedPrompt, selectedProvider, setSelectedProvider])

  return {
    selectedProvider,
    setSelectedProvider,
  }
}

/**
 * Custom hook to manage prompt selection
 * Always shows all available prompts
 */
export function usePromptSelect() {
  const [selectedPrompt, setSelectedPrompt] = useAtom(selectedPromptAtom)
  // Always get all prompts
  const { filteredPrompts, loading } = useFilteredPromptsV2()

  const handlePromptChange = useCallback(
    (promptId: string | null) => {
      if (promptId) {
        const prompt = filteredPrompts.find((p) => p.promptId === promptId)
        if (prompt) {
          setSelectedPrompt(prompt)
        }
      } else {
        setSelectedPrompt(null)
      }
    },
    [filteredPrompts, setSelectedPrompt],
  )

  return {
    selectedPromptId: selectedPrompt?.promptId || null,
    selectedPrompt,
    filteredPrompts,
    loading,
    handlePromptChange,
  }
}

/**
 * Custom hook to manage model selection and sync with prompt
 */
export function useModelSelect() {
  const [selectedModel, setSelectedModel] = useAtom(selectedModelAtom)
  const selectedModelValue = useAtomValue(selectedModelValueAtom)
  const selectedPrompt = useAtomValue(selectedPromptAtom)
  const prevPromptIdRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (selectedPrompt?.metadata.default_model) {
      const currentPromptId = selectedPrompt.promptId
      const promptChanged = prevPromptIdRef.current !== currentPromptId

      if (promptChanged || !selectedModel) {
        setSelectedModel(selectedPrompt.metadata.default_model)
        prevPromptIdRef.current = currentPromptId
      }
    }
  }, [selectedPrompt, selectedModel, setSelectedModel])

  return {
    selectedModel,
    selectedModelValue,
    selectedPrompt,
    setSelectedModel,
  }
}

/**
 * Custom hook to manage pipeline setup configuration
 */
export function useSetupPipeline() {
  const [processMode, setProcessMode] = useAtom(processModeAtom)
  const [fieldTitle, setFieldTitle] = useAtom(fieldTitleAtom)
  const [fieldDescription, setFieldDescription] = useAtom(fieldDescriptionAtom)
  const [fieldBodyValue, setFieldBodyValue] = useAtom(fieldBodyValueAtom)
  const [studentFacing, setStudentFacing] = useAtom(studentFacingAtom)
  const [batchMode, setBatchMode] = useAtom(batchModeAtom)

  const handleProcessModeChange = useCallback(
    (mode: ProcessMode) => {
      setProcessMode(mode)
    },
    [setProcessMode],
  )

  const handleBatchModeChange = useCallback(
    (mode: BatchMode) => {
      setBatchMode(mode)
    },
    [setBatchMode],
  )

  return {
    processMode,
    fieldTitle,
    fieldDescription,
    fieldBodyValue,
    studentFacing,
    batchMode,
    setFieldTitle,
    setFieldDescription,
    setFieldBodyValue,
    setStudentFacing,
    handleProcessModeChange,
    handleBatchModeChange,
  }
}

/**
 * Custom hook to manage prompt runner state and operations
 */
export function usePromptRunner(_area?: string) {
  const selectedPrompt = useAtomValue(selectedPromptAtom)
  const selectedModel = useAtomValue(selectedModelValueAtom)
  const selectedProvider = useAtomValue(selectedProviderAtom)
  const [userPrompts, setUserPrompts] = useState<string[]>([''])
  const [modelOverride, setModelOverride] = useState<string>(
    selectedModel || '',
  )
  const [providerOverride, setProviderOverride] = useState<string>(
    selectedProvider || '',
  )

  const setResponse = useSetAtom(promptRunResponseAtom)
  const setRequestParams = useSetAtom(promptRunRequestParamsAtom)
  const setError = useSetAtom(promptRunErrorAtom)
  const setRunning = useSetAtom(promptRunRunningAtom)

  const result = useAtomValue(promptRunResponseAtom)
  const rawResultJson = useAtomValue(promptRunRawResultJsonAtom)
  const error = useAtomValue(promptRunErrorAtom)
  const running = useAtomValue(promptRunRunningAtom)

  useEffect(() => {
    if (selectedModel) {
      setModelOverride(selectedModel)
    }
  }, [selectedModel])

  useEffect(() => {
    if (selectedProvider) {
      setProviderOverride(selectedProvider)
    }
  }, [selectedProvider])

  const handleAddPrompt = useCallback(() => {
    setUserPrompts([...userPrompts, ''])
  }, [userPrompts])

  const handleRemovePrompt = useCallback(
    (index: number) => {
      setUserPrompts(userPrompts.filter((_, i) => i !== index))
    },
    [userPrompts],
  )

  const handlePromptChange = useCallback(
    (index: number, value: string) => {
      const newPrompts = [...userPrompts]
      newPrompts[index] = value
      setUserPrompts(newPrompts)
    },
    [userPrompts],
  )

  const handleRunPrompt = useCallback(async () => {
    if (!selectedPrompt) {
      setError('Please select a prompt')
      return
    }

    const model = modelOverride.trim()
    const provider = providerOverride.trim()

    if (!model) {
      setError('Please enter a model')
      return
    }

    if (!provider) {
      setError('Please enter a provider')
      return
    }

    const nonEmptyPrompts = userPrompts.filter((p) => p.trim() !== '')
    if (nonEmptyPrompts.length === 0) {
      setError('Please add at least one user prompt')
      return
    }

    const userPromptsArray = Array.isArray(nonEmptyPrompts)
      ? nonEmptyPrompts
      : [nonEmptyPrompts]

    setRunning(true)
    setError(null)
    setResponse(null)
    setRequestParams(null)

    const params = {
      area: selectedPrompt.area,
      prompt_id: selectedPrompt.promptId,
      model,
      provider,
      user_prompts: userPromptsArray,
    }
    setRequestParams(params)

    try {
      const response = await runPrompt(
        selectedPrompt.area,
        selectedPrompt.promptId,
        model,
        provider,
        userPromptsArray,
      )
      setResponse(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run prompt')
    } finally {
      setRunning(false)
    }
  }, [
    selectedPrompt,
    modelOverride,
    providerOverride,
    userPrompts,
    setError,
    setRunning,
    setResponse,
    setRequestParams,
  ])

  return {
    selectedPrompt,
    modelOverride,
    providerOverride,
    userPrompts,
    result,
    rawResultJson,
    error,
    running,
    setModelOverride,
    setProviderOverride,
    handleAddPrompt,
    handleRemovePrompt,
    handlePromptChange,
    handleRunPrompt,
  }
}

// Helper to extract entries from a field array
const extractFieldEntries = <
  T extends CorrectionEntryItem | UnchangedEntryItem,
>(
  fieldArray: unknown[] | undefined,
  fieldType: 'title' | 'description' | 'body',
  keyPrefix: string,
): T[] => {
  if (!fieldArray) {
    return []
  }

  return fieldArray.map((entry, entryIndex) => ({
    fieldType,
    entry,
    entryIndex,
    key: keyPrefix
      ? `${keyPrefix}-${fieldType}-${entryIndex}`
      : `${fieldType}-${entryIndex}`,
  })) as T[]
}

// Helper to process entries from result data
const processEntries = <T extends CorrectionEntryItem | UnchangedEntryItem>(
  result: { title?: unknown[]; description?: unknown[]; body?: unknown[] },
  keyPrefix: string,
): T[] => {
  const entries: T[] = []

  entries.push(
    ...extractFieldEntries<T>(result.title, 'title', keyPrefix),
    ...extractFieldEntries<T>(result.description, 'description', keyPrefix),
    ...extractFieldEntries<T>(result.body, 'body', keyPrefix),
  )

  // Sort by field type order, then by entry index
  entries.sort((a, b) => {
    const orderA = getFieldSortOrder(a.fieldType)
    const orderB = getFieldSortOrder(b.fieldType)
    if (orderA !== orderB) {
      return orderA - orderB
    }
    return a.entryIndex - b.entryIndex
  })

  return entries
}

// Helper to get the first result from processResult
const getFirstResult = (
  processResult: ProcessResultData | null,
): ProcessResult | null => {
  if (!processResult || !processResult.results?.[0]) {
    return null
  }
  return processResult.results[0]?.result || null
}

/**
 * Custom hook to manage review corrections data processing
 */
export function useReviewCorrections() {
  const processResult = useAtomValue(processResultAtom)
  const applyStatus = useAtomValue(applyStatusAtom)

  const correctionEntries = useMemo(() => {
    const result = getFirstResult(processResult)
    if (!result?.corrected) {
      return []
    }

    return processEntries<CorrectionEntryItem>(result.corrected, '')
  }, [processResult])

  const unchangedEntries = useMemo(() => {
    const result = getFirstResult(processResult)
    if (!result?.unchanged) {
      return []
    }

    return processEntries<UnchangedEntryItem>(result.unchanged, 'unchanged')
  }, [processResult])

  const { selectedCorrections, handleSelectAll, handleSelectCorrection } =
    useCorrectionSelection(correctionEntries)

  const selectedCount = selectedCorrections.size
  const allSelected =
    correctionEntries.length > 0 && selectedCount === correctionEntries.length
  const someSelected =
    selectedCount > 0 && selectedCount < correctionEntries.length

  return {
    processResult,
    applyStatus,
    correctionEntries,
    unchangedEntries,
    selectedCorrections,
    selectedCount,
    allSelected,
    someSelected,
    handleSelectAll,
    handleSelectCorrection,
  }
}
